import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Shell from "../Shell";
import api, { authHeaders } from "../api";

/* ───────────── Date helpers ───────────── */

const isoToDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dateToIso = (date) => {
  if (!(date instanceof Date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fmtDMY = (iso) => {
  const dt = isoToDate(iso);
  if (!dt) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

function fmtDate(iso) {
  if (!iso) return "—";
  const dt = isoToDate(iso);
  if (!dt) return iso;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/* ───────────── Date range helpers ───────────── */

function startOfWeekMonday(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d;
}

function endOfWeekSunday(date) {
  const d = startOfWeekMonday(date);
  d.setDate(d.getDate() + 6);
  return d;
}

function firstDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function lastDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function applyLastWeek(setStart, setEnd) {
  const today = new Date();
  const thisWeekStart = startOfWeekMonday(today);
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(thisWeekStart.getDate() - 7);
  const lastWeekEnd = endOfWeekSunday(lastWeekStart);
  setStart(dateToIso(lastWeekStart));
  setEnd(dateToIso(lastWeekEnd));
}

function applyPastWeek(setStart, setEnd) {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  start.setDate(start.getDate() - 6);
  setStart(dateToIso(start));
  setEnd(dateToIso(today));
}

function applyLastMonth(setStart, setEnd) {
  const today = new Date();
  const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  setStart(dateToIso(firstDayOfMonth(prevMonthDate)));
  setEnd(dateToIso(lastDayOfMonth(prevMonthDate)));
}

function applyPastMonth(setStart, setEnd) {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  start.setDate(start.getDate() - 29);
  setStart(dateToIso(start));
  setEnd(dateToIso(today));
}

/* ───────────── Mini date picker ───────────── */

function MiniDatePickerDMY({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = isoToDate(value) || new Date();
  const [view, setView] = useState(new Date(selected.getFullYear(), selected.getMonth(), 1));
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const s = isoToDate(value);
    if (s) setView(new Date(s.getFullYear(), s.getMonth(), 1));
  }, [value]);

  const startOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const startDay = (startOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();

  const prevMonth = () => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  const nextMonth = () => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.getFullYear(), view.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);

  const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <input
        readOnly
        value={fmtDMY(value)}
        onClick={() => setOpen((v) => !v)}
        style={{ width: 160, cursor: "pointer" }}
        placeholder="dd/mm/áááá"
      />
      {open && (
        <div
          style={{
            position: "absolute",
            top: 38,
            left: 0,
            background: "#fff",
            border: "1px solid #d7dbe3",
            borderRadius: 8,
            padding: 8,
            zIndex: 50,
            boxShadow: "0 12px 28px rgba(0,0,0,.12)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>
            <button className="btn ghost sm" type="button" onClick={prevMonth}>◀</button>
            <div style={{ fontWeight: 600 }}>
              {view.toLocaleString("is-IS", { month: "long" })} {view.getFullYear()}
            </div>
            <button className="btn ghost sm" type="button" onClick={nextMonth}>▶</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 32px)", gap: 4, textAlign: "center", opacity: 0.7, fontSize: 12, marginBottom: 4 }}>
            {["Mán", "Þri", "Mið", "Fim", "Fös", "Lau", "Sun"].map((d) => <div key={d}>{d}</div>)}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 32px)", gap: 4, textAlign: "center" }}>
            {cells.map((c, i) => {
              const isOut = c === null || c.getMonth() !== view.getMonth();
              const active = c && isSameDay(c, selected);
              return (
                <button
                  key={i}
                  type="button"
                  disabled={!c}
                  onClick={() => {
                    if (!c) return;
                    onChange?.(dateToIso(c));
                    setOpen(false);
                  }}
                  style={{
                    width: 32,
                    height: 28,
                    borderRadius: 6,
                    border: "1px solid #d7dbe3",
                    opacity: isOut ? 0.35 : 1,
                    background: active ? "#e0ecff" : "#fff",
                    cursor: c ? "pointer" : "default",
                  }}
                >
                  {c ? c.getDate() : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────── Debounce ───────────── */

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/* ───────────── Component ───────────── */

export default function AttendanceSummary() {
  const todayIso = dateToIso(new Date());

  const [q, setQ] = useState("");
  const [searchIn, setSearchIn] = useState("all");
  const [start, setStart] = useState(todayIso);
  const [end, setEnd] = useState(todayIso);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [uniqueOnly, setUniqueOnly] = useState(false);
  const [minCount, setMinCount] = useState("");
  const [rehabFilter, setRehabFilter] = useState("");

  const dq = useDebounced(q, 350);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = {
      start,
      end,
      limit,
      offset,
      order: "desc",
    };

    if (dq.trim()) p.q = dq.trim();
    if (searchIn) p.search_in = searchIn;
    if (rehabFilter !== "") p.in_rehab = rehabFilter;

    if (uniqueOnly) {
      p.unique = "username";
      if (minCount !== "" && Number(minCount) > 0) {
        p.min_count = Number(minCount);
      }
    }

    return p;
  }, [start, end, limit, offset, dq, searchIn, rehabFilter, uniqueOnly, minCount]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/attendance/summary/", {
        params,
        headers: authHeaders(),
      });
      setRows(Array.isArray(data?.results) ? data.results : []);
      setTotal(data?.total ?? 0);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setOffset(0);
  }, [dq, searchIn, start, end, limit, uniqueOnly, minCount, rehabFilter]);

  useEffect(() => {
    if (!uniqueOnly && minCount !== "") {
      setMinCount("");
    }
  }, [uniqueOnly, minCount]);

  const onPrev = () => setOffset((o) => Math.max(0, o - limit));
  const onNext = () => setOffset((o) => o + limit);

  const colSpan = uniqueOnly ? 8 : 7;

  return (
    <Shell rightMenu>
      <h2 style={{ textAlign: "center", margin: "8px 0 18px" }}>Mæting</h2>

      <div className="members-toolbar" style={{ justifyContent: "space-between" }}>
        <div className="search-block" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label className="sr" htmlFor="search">Leita</label>
          <input
            id="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Leita í mætingu"
            style={{ width: 220 }}
          />

          <select value={searchIn} onChange={(e) => setSearchIn(e.target.value)}>
            <option value="all">Leita í öllum dálkum</option>
            <option value="group_name">Aðeins hópnafn</option>
            <option value="user_full_name">Aðeins nafn</option>
            <option value="username">Aðeins notendanafn</option>
            <option value="ssn">Aðeins kennitala</option>
            <option value="marked_by">Aðeins skráði mætingu</option>
            <option value="date">Aðeins dagsetning</option>
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              border: "2px solid #222",
              borderRadius: 6,
              padding: "6px 12px",
              textAlign: "center",
              minWidth: 160,
              fontWeight: 600,
            }}
          >
            Mæting samtals
            <div style={{ fontSize: 18 }}>{total}</div>
          </div>
        </div>

        <div className="filter-block">
          <button className="btn sm" onClick={() => setFiltersOpen((v) => !v)}>
            Sía/röð
          </button>

          {filtersOpen && (
            <div className="filter-popover" style={{ minWidth: 360 }}>
              <div className="filter-title">Dagsetningar:</div>

              <div className="filter-row">
                <label>Frá:</label>
                <MiniDatePickerDMY value={start} onChange={setStart} />
              </div>

              <div className="filter-row">
                <label>Til:</label>
                <MiniDatePickerDMY value={end} onChange={setEnd} />
              </div>

              <div className="filter-title" style={{ marginTop: 10 }}>Flýtihnappar:</div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
                <button className="btn ghost sm" onClick={() => applyLastWeek(setStart, setEnd)}>
                  Síðasta vika
                </button>
                <button className="btn ghost sm" onClick={() => applyPastWeek(setStart, setEnd)}>
                  Síðustu 7 dagar
                </button>
                <button className="btn ghost sm" onClick={() => applyLastMonth(setStart, setEnd)}>
                  Síðasti mánuður
                </button>
                <button className="btn ghost sm" onClick={() => applyPastMonth(setStart, setEnd)}>
                  Síðustu 30 dagar
                </button>
              </div>

              <div className="filter-title" style={{ marginTop: 10 }}>Einstakar færslur:</div>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={uniqueOnly}
                  onChange={(e) => setUniqueOnly(e.target.checked)}
                />
                Sýna einstaka notendur
              </label>

              <div className="filter-title" style={{ marginTop: 10, opacity: uniqueOnly ? 1 : 0.6 }}>
                Lágmarksfjöldi mætinga:
              </div>
              <input
                type="number"
                min="0"
                value={minCount}
                onChange={(e) => setMinCount(e.target.value)}
                placeholder="t.d. 3"
                style={{ width: "100%" }}
                disabled={!uniqueOnly}
              />

              <div className="filter-title" style={{ marginTop: 10 }}>Endurhæfing:</div>
              <select value={rehabFilter} onChange={(e) => setRehabFilter(e.target.value)}>
                <option value="">Allir</option>
                <option value="1">Aðeins í endurhæfingu</option>
                <option value="0">Aðeins ekki í endurhæfingu</option>
              </select>

              <div className="filter-title" style={{ marginTop: 10 }}>Fjöldi á síðu:</div>
              <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}>
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
                <button
                  className="btn ghost sm"
                  onClick={() => {
                    setQ("");
                    setSearchIn("all");
                    setStart(todayIso);
                    setEnd(todayIso);
                    setLimit(100);
                    setUniqueOnly(false);
                    setMinCount("");
                    setRehabFilter("");
                  }}
                >
                  Hreinsa
                </button>
                <button className="btn ghost sm" onClick={() => setFiltersOpen(false)}>
                  Loka síu
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Hleð…</div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table zebra">
              <thead>
                <tr>
                  <th>Hópnafn</th>
                  <th>Nafn</th>
                  <th>Notendanafn</th>
                  <th>Kennitala</th>
                  <th>Í endurhæfingu</th>
                  {uniqueOnly && <th>Fjöldi mætinga</th>}
                  <th>Skráði mætingu</th>
                  <th>Dagsetning</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} style={{ textAlign: "center", padding: 16 }}>
                      Engin mæting fannst fyrir valið tímabil
                    </td>
                  </tr>
                ) : rows.map((r, i) => (
                  <tr key={`${r.kind || "att"}-${r.username}-${r.date}-${i}`}>
                    <td>{r.group_name}</td>
                    <td>{r.user_full_name}</td>
                    <td>{r.username}</td>
                    <td>{r.ssn}</td>
                    <td>{r.is_in_rehab ? "Já" : "Nei"}</td>
                    {uniqueOnly && <td>{r.count ?? "—"}</td>}
                    <td>{r.marked_by}</td>
                    <td>{fmtDate(r.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
            <div style={{ opacity: 0.8 }}>
              Sýni {rows.length} af {total}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn sm ghost" onClick={onPrev} disabled={offset === 0}>Fyrri</button>
              <button className="btn sm" onClick={onNext} disabled={offset + limit >= total}>Næsta</button>
            </div>
          </div>
        </>
      )}
    </Shell>
  );
}